import React, { useCallback, useState } from "react";
import { Button, Space, Spin, Tag, Typography, message } from "antd";
import { DownloadOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  LeanCustomsService,
  type LeanCustomsDeclaration,
  type LeanCustomsDeclarationDocument,
  type LeanCustomsDeclarationMessage,
  type LeanCustomsInvoice,
} from "../../services/leancustomsService";
import { LEANCUSTOMS_DECLARATION_BASE } from "../../constants/leancustoms";
import {
  DECLARATION_STATUS_COLORS,
  DECLARATION_STATUS_LABELS,
} from "../../types/leancustoms";
import { extractUtbTtwAmountDisplays } from "../../utils/leanCustomsUtbTtwAmounts";
import { formatDeclarationInvoiceTotals } from "../../utils/leanCustomsInvoiceTotals";
import {
  getDeclarationDocumentOfficialKind,
} from "../../utils/leanCustomsDeclarationDocumentsSort";
import {
  pickMessageBody,
  pickMessageDate,
  pickMessageKind,
} from "../../utils/leanCustomsMessageDisplay";
import { formatByteSize } from "../../utils/leanCustomsRecordUtils";

export interface DeclarationEnrichment {
  invoices: LeanCustomsInvoice[];
  documents: LeanCustomsDeclarationDocument[];
  itemCount: number;
  messages: LeanCustomsDeclarationMessage[];
}

export interface BookingCustomsLinkedDeclarationPanelProps {
  declaration: LeanCustomsDeclaration;
  companyId: number;
  savedDeclarationId?: string | null;
  linkingDeclarationId: string | null;
  enrichment: DeclarationEnrichment | null;
  enrichmentLoading: boolean;
  onLink: (declarationId: string) => void;
}

const RECENT_MESSAGE_LIMIT = 3;

export const BookingCustomsLinkedDeclarationPanel: React.FC<
  BookingCustomsLinkedDeclarationPanelProps
> = ({
  declaration: decl,
  companyId,
  savedDeclarationId,
  linkingDeclarationId,
  enrichment,
  enrichmentLoading,
  onLink,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const status = decl.dms_status || "unknown";
  const statusColor = DECLARATION_STATUS_COLORS[status] || "default";
  const statusLabel = DECLARATION_STATUS_LABELS[status] || status;
  const isSavedOnBooking =
    savedDeclarationId != null && String(savedDeclarationId) === String(decl.id);

  const amounts = enrichment
    ? extractUtbTtwAmountDisplays(decl, enrichment.invoices)
    : { utbPayment: null, ttwSurety: null };
  const invoiceTotal = enrichment
    ? formatDeclarationInvoiceTotals(enrichment.invoices)
    : null;

  const handleDownload = useCallback(
    async (docId: string, filename: string) => {
      setDownloadingId(docId);
      try {
        const blob = await LeanCustomsService.downloadDeclarationDocument(
          decl.id,
          docId,
          companyId
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success("Document downloaded");
      } catch (err: unknown) {
        message.error(err instanceof Error ? err.message : "Download failed");
      } finally {
        setDownloadingId(null);
      }
    },
    [decl.id, companyId]
  );

  return (
    <div>
      <Space wrap size={[8, 4]}>
        <Typography.Text code style={{ fontSize: 12 }}>
          {decl.id}
        </Typography.Text>
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          href={`${LEANCUSTOMS_DECLARATION_BASE}/${decl.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ padding: 0, height: "auto" }}
        >
          Open in Lean Customs
        </Button>
        {isSavedOnBooking ? (
          <Tag color="green">On booking</Tag>
        ) : (
          <Button
            type="link"
            size="small"
            loading={linkingDeclarationId === decl.id}
            onClick={() => onLink(decl.id)}
            style={{ padding: 0, height: "auto" }}
          >
            Link to booking
          </Button>
        )}
      </Space>

      <div style={{ marginTop: 4 }}>
        <Space wrap size={[8, 4]}>
          {decl.reference ? (
            <Typography.Text style={{ fontSize: 12 }}>{decl.reference}</Typography.Text>
          ) : null}
          {decl.declaration_type ? <Tag>{decl.declaration_type}</Tag> : null}
          <Tag color={statusColor}>{statusLabel}</Tag>
        </Space>
      </div>

      {(decl.mrn || decl.lrn) && (
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginTop: 4 }}
        >
          {[decl.mrn ? `MRN ${decl.mrn}` : null, decl.lrn ? `LRN ${decl.lrn}` : null]
            .filter(Boolean)
            .join(" · ")}
        </Typography.Text>
      )}

      {(decl.acceptance_date || decl.clearance_date || decl.release_date) && (
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginTop: 2 }}
        >
          {[
            decl.acceptance_date
              ? `Accepted ${dayjs(decl.acceptance_date).format("YYYY-MM-DD")}`
              : null,
            decl.clearance_date
              ? `Cleared ${dayjs(decl.clearance_date).format("YYYY-MM-DD")}`
              : null,
            decl.release_date
              ? `Released ${dayjs(decl.release_date).format("YYYY-MM-DD")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography.Text>
      )}

      {enrichmentLoading ? (
        <div style={{ marginTop: 8 }}>
          <Spin size="small" />
        </div>
      ) : enrichment ? (
        <div style={{ marginTop: 8 }}>
          {(amounts.utbPayment || amounts.ttwSurety || invoiceTotal) && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: "block" }}
            >
              {[
                amounts.utbPayment ? `UTB ${amounts.utbPayment}` : null,
                amounts.ttwSurety ? `TTW ${amounts.ttwSurety}` : null,
                invoiceTotal ? `Invoice total ${invoiceTotal}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography.Text>
          )}

          {enrichment.itemCount > 0 ? (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginTop: 4 }}
            >
              {enrichment.itemCount} declaration line item
              {enrichment.itemCount === 1 ? "" : "s"}
            </Typography.Text>
          ) : null}

          {enrichment.documents.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <Typography.Text strong style={{ fontSize: 12 }}>
                Documents
              </Typography.Text>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {enrichment.documents.map((doc) => {
                  const kind = getDeclarationDocumentOfficialKind(doc);
                  return (
                    <li key={doc.id} style={{ marginBottom: 4 }}>
                      <Space wrap size={4}>
                        {kind === "utb" ? <Tag color="blue">UTB</Tag> : null}
                        {kind === "ttw" ? <Tag color="blue">TTW</Tag> : null}
                        <span style={{ fontSize: 12 }}>{doc.filename}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {formatByteSize(doc.byte_size ?? 0)}
                        </Typography.Text>
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          loading={downloadingId === doc.id}
                          onClick={() =>
                            handleDownload(doc.id, doc.filename || "document")
                          }
                          style={{ padding: 0, height: "auto", fontSize: 12 }}
                        >
                          Download
                        </Button>
                      </Space>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {enrichment.messages.length > 0 ? (
            <div style={{ marginTop: 6 }}>
              <Typography.Text strong style={{ fontSize: 12 }}>
                Recent customs messages
              </Typography.Text>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {enrichment.messages.slice(0, RECENT_MESSAGE_LIMIT).map((msg, index) => {
                  const body = pickMessageBody(msg);
                  const when = pickMessageDate(msg);
                  return (
                    <li key={msg.id ?? `msg-${index}`} style={{ marginBottom: 4 }}>
                      <Typography.Text style={{ fontSize: 12 }}>
                        {when ? dayjs(when).format("YYYY-MM-DD HH:mm") : "—"} ·{" "}
                        {pickMessageKind(msg)}
                        {body ? ` — ${body}` : ""}
                      </Typography.Text>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
